import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, BedDouble, MapPin, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../i18n";
import { API, IMAGES, imgUrl, LINKS, propertyUrl, trackEvent } from "../data";

const EASE = [0.22, 1, 0.36, 1];

export default function Properties() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const p = t.properties;
  const [dbItems, setDbItems] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/properties`)
      .then((r) => setDbItems(r.data))
      .catch(() => setDbItems([]));
  }, []);

  const list =
    dbItems && dbItems.length
      ? dbItems.map((it) => ({
          id: it.id || it._id || it.name,
          name: it.name,
          location: it.location,
          guests: it.guests,
          rooms: it.rooms,
          price: it.price,
          image: it.images && it.images.length ? it.images[0] : null,
          url: propertyUrl(it),
        }))
      : IMAGES.properties.map((prop, i) => ({
          id: prop.name,
          name: prop.name,
          image: prop.image,
          url: LINKS.booking,
          ...p.items[i],
        }));

  return (
    <section id="case" data-testid="properties-section" className="bg-sand py-28 sm:py-36 lg:py-44">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-120px" }}
          transition={{ duration: 1, ease: EASE }}
          className="flex flex-wrap items-end justify-between gap-8"
        >
          <div className="max-w-2xl">
            <p className="kicker text-navy">
              <span className="h-px w-10 bg-sky" />
              {p.tag}
            </p>
            <h2 className="mt-6 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-navy sm:text-5xl lg:text-6xl">
              {p.title}
            </h2>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-mute sm:text-lg">{p.sub}</p>
          </div>
          <button
            data-testid="properties-view-all"
            onClick={() => navigate("/strutture")}
            className="group inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] text-navy transition-colors hover:text-sky"
          >
            {p.cta}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
          </button>
        </motion.div>

        <div className="mt-16 grid gap-10 sm:grid-cols-2 lg:mt-20 lg:grid-cols-3 lg:gap-12">
          {list.map((prop, i) => (
            <motion.a
              key={prop.id}
              href={prop.url}
              data-testid={`property-card-${i}`}
              data-property-id={prop.id}
              onClick={() => trackEvent("property_booking_click", { property_id: String(prop.id), property_name: prop.name, source: "homepage" })}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1, delay: (i % 3) * 0.15, ease: EASE }}
              className="group block cursor-pointer"
            >
              <div className="overflow-hidden rounded-t-[10rem] rounded-b-[2rem] ring-1 ring-navy/10 transition-shadow duration-500 group-hover:shadow-[0_30px_60px_-20px_rgba(15,43,58,0.35)]">
                <div className="aspect-[4/5] overflow-hidden">
                  <img
                    src={imgUrl(prop.image)}
                    alt={prop.name}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.07]"
                  />
                </div>
              </div>
              <div className="mt-6 flex items-start justify-between gap-4 px-1">
                <div>
                  <h3 className="font-display text-2xl font-semibold text-navy">{prop.name}</h3>
                  <p className="mt-2 flex items-center gap-1.5 text-sm font-semibold text-mute">
                    <MapPin className="h-3.5 w-3.5 text-sky" />
                    {prop.location}
                  </p>
                  <p className="mt-2 flex items-center gap-4 text-xs font-bold uppercase tracking-wider text-mute">
                    <span className="flex items-center gap-1.5">
                      <Users className="h-3.5 w-3.5" />
                      {prop.guests} {p.guests}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <BedDouble className="h-3.5 w-3.5" />
                      {prop.rooms} {p.rooms}
                    </span>
                  </p>
                  <span className="mt-3 inline-flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.16em] text-navy transition-colors group-hover:text-sky">
                    {p.checkAvailability}
                    <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                  </span>
                </div>
                {prop.price && (
                  <div className="shrink-0 rounded-full bg-navy px-4 py-2 text-right">
                    <span className="block text-[10px] font-bold uppercase tracking-widest text-sky">{p.from}</span>
                    <span className="text-sm font-extrabold text-white">
                      {prop.price} <span className="text-[10px] font-semibold text-white/60">{p.night}</span>
                    </span>
                  </div>
                )}
              </div>
            </motion.a>
          ))}

          <motion.a
            href={LINKS.booking}
            data-testid="properties-cta-card"
            onClick={() => trackEvent("property_booking_click", { property_id: "all", property_name: "all", source: "homepage" })}
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, delay: 0.3, ease: EASE }}
            className="group flex min-h-[26rem] cursor-pointer flex-col items-center justify-center gap-6 rounded-t-[10rem] rounded-b-[2rem] bg-navy px-8 text-center transition-colors duration-500 hover:bg-med sm:col-span-2 lg:col-span-1"
          >
            <span className="kicker text-sky">{p.tag}</span>
            <h3 className="font-display text-3xl font-semibold text-white">{p.ctaCardTitle}</h3>
            <p className="text-sm text-white/60">{p.ctaCardSub}</p>
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-sky text-navy transition-transform duration-300 group-hover:scale-110">
              <ArrowRight className="h-5 w-5" />
            </span>
          </motion.a>
        </div>
      </div>
    </section>
  );
}
